<section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
  <div class="flex flex-wrap items-center justify-between gap-3">
    <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'vehicles.photosDocuments' | t }}</h2>
    <label class="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-50">
      @if (uploading()) {
        <svg class="size-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"></circle>
          <path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" class="opacity-75"></path>
        </svg>
        Upload en cours…
      } @else {
        {{ 'vehicles.ajouterDesPhotos' | t }}
      }
      <input
        type="file"
        [attr.accept]="acceptAttr"
        multiple
        class="hidden"
        [disabled]="uploading()"
        (change)="onFilesSelected(uploadInput)"
        #uploadInput
      />
    </label>
  </div>

  <!-- Tabs catégorie -->
  <div class="mt-4 flex flex-wrap gap-2">
    @for (opt of typeOptions; track opt.value) {
      <button
        type="button"
        (click)="switchType(opt.value)"
        class="rounded-full border px-3 py-1 text-xs font-medium transition"
        [class.border-brand-500]="activeType() === opt.value"
        [class.bg-brand-500]="activeType() === opt.value"
        [class.text-white]="activeType() === opt.value"
        [class.border-gray-300]="activeType() !== opt.value"
        [class.text-gray-700]="activeType() !== opt.value"
        [class.hover:bg-gray-50]="activeType() !== opt.value"
        [class.dark:border-gray-700]="activeType() !== opt.value"
        [class.dark:text-gray-300]="activeType() !== opt.value"
        [class.dark:hover:bg-white/5]="activeType() !== opt.value"
      >
        {{ opt.label | t }}
      </button>
    }
  </div>

  @if (uploadError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ uploadError() }}
    </div>
  }
  @if (error()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ error() }}
    </div>
  }

  <div class="mt-5">
    @if (loading()) {
      <p class="text-sm text-gray-400">{{ 'vehicles.chargement' | t }}</p>
    } @else if (photos().length === 0) {
      <p class="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-white/[0.02] dark:text-gray-400">
        {{ 'vehicles.noPhotoInCategory' | t: { category: (typeLabel[activeType()] | t) } }}
      </p>
    } @else {
      <div class="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
        @for (photo of photos(); track photo.id) {
          <div class="group relative overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-white/[0.02]">
            <!-- Aperçu : image cliquable (lightbox) ou placeholder document -->
            <div class="relative aspect-[4/3] w-full overflow-hidden">
              @if (isDocument(photo.url)) {
                <button
                  type="button"
                  (click)="openDoc(photo.url)"
                  class="flex h-full w-full flex-col items-center justify-center gap-2 bg-gray-100 text-gray-500 transition hover:bg-gray-200 dark:bg-white/[0.04] dark:text-gray-400 dark:hover:bg-white/[0.08]"
                  [title]="'vehicles.ouvrirLeDocument' | t"
                >
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path d="M8 3.75A1.25 1.25 0 0 0 6.75 5v14A1.25 1.25 0 0 0 8 20.25h8A1.25 1.25 0 0 0 17.25 19V8.5L13 3.75H8Z" stroke-linejoin="round"/>
                    <path d="M12.75 4v4.5h4.25M9 13h6M9 16h4" stroke-linecap="round"/>
                  </svg>
                  <span class="text-[11px] font-medium">{{ 'vehicles.voirLeDocument' | t }}</span>
                </button>
              } @else {
                <img
                  [src]="photo.url"
                  alt=""
                  (click)="openLightbox(photo.url)"
                  class="h-full w-full cursor-zoom-in object-cover transition group-hover:scale-105"
                />
              }
              @if (isCurrentCover(photo)) {
                <span class="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-brand-500 px-2 py-0.5 text-[10px] font-medium text-white shadow-sm">
                  ★ Couverture
                </span>
              }
              @if (busyPhotoId() === photo.id) {
                <span class="absolute inset-0 flex items-center justify-center bg-black/40">
                  <svg class="size-6 animate-spin text-white" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"></circle>
                    <path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" class="opacity-75"></path>
                  </svg>
                </span>
              }
            </div>

            <!-- Actions -->
            <div class="space-y-2 border-t border-gray-200 p-2 dark:border-gray-700">
              @if (activeType() === 'GENERAL' && !isCurrentCover(photo)) {
                <button
                  type="button"
                  (click)="setAsCover(photo)"
                  [disabled]="busyPhotoId() !== null"
                  class="w-full rounded-lg border border-brand-300 px-2 py-1 text-[11px] font-medium text-brand-700 transition hover:bg-brand-50 disabled:opacity-50 dark:border-brand-800 dark:text-brand-400 dark:hover:bg-brand-500/10"
                >
                  {{ 'vehicles.definirCommeCouverture' | t }}
                </button>
              }
              <div class="flex items-center gap-1">
                <select
                  [value]="photo.type ?? 'GENERAL'"
                  (change)="changePhotoType(photo, $any($event.target).value)"
                  [disabled]="busyPhotoId() !== null"
                  class="h-7 flex-1 rounded-md border border-gray-300 bg-transparent px-1.5 text-[11px] text-gray-700 focus:border-brand-300 focus:outline-hidden focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
                >
                  @for (opt of typeOptions; track opt.value) {
                    <option [value]="opt.value">{{ opt.label | t }}</option>
                  }
                </select>
                <button
                  type="button"
                  (click)="openDelete(photo)"
                  [disabled]="busyPhotoId() !== null || isCurrentCover(photo)"
                  [title]="isCurrentCover(photo) ? 'Choisis une autre couverture avant de supprimer' : 'Supprimer'"
                  class="rounded-md border border-error-300 px-1.5 py-1 text-error-700 transition hover:bg-error-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-error-800 dark:text-error-400 dark:hover:bg-error-500/10"
                >
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path d="M8.75 2.5h2.5a.75.75 0 0 1 .75.75V4h3.75a.75.75 0 0 1 0 1.5h-.46l-.7 10.04A2.25 2.25 0 0 1 12.34 17.5H7.66a2.25 2.25 0 0 1-2.25-1.96L4.71 5.5H4.25a.75.75 0 0 1 0-1.5H8V3.25a.75.75 0 0 1 .75-.75ZM6.21 5.5l.69 9.93a.75.75 0 0 0 .75.65h4.7a.75.75 0 0 0 .75-.65l.7-9.93H6.21Z"/></svg>
                </button>
              </div>
            </div>
          </div>
        }
      </div>
    }
  </div>
</section>

<app-confirm-modal
  [open]="deleteOpen()"
  [busy]="deleting()"
  [error]="deleteError()"
  [title]="'vehicles.supprimerCettePhoto' | t"
  [confirmLabel]="'vehicles.supprimerDefinitivement' | t"
  busyLabel="Suppression…"
  danger
  (confirm)="confirmDelete()"
  (close)="closeDelete()"
>
  {{ 'vehicles.laPhotoSeraRetiree' | t }}
</app-confirm-modal>

<!-- Lightbox plein écran pour les images -->
<app-image-lightbox [src]="lightboxSrc()" (close)="lightboxSrc.set(null)" />
